import React, { createContext, useContext } from 'react'

import { type EmployeeInfo } from 'components/quoting/quotes/employee_entry/EmployeeEntry'
import { PutPayrollPaySchedulesIdBankClosureStrategy as BankClosureStrategy } from 'utils/web_api.schemas'

import { useFlags } from './FlagProvider'

export type PrefillData = {
  // Register Page
  email?: string
  password?: string
  password_confirmation?: string
  agree_to_e_sign_agreement?: boolean
  agree_to_terms?: boolean

  // Basic Info Page
  first_name?: string
  last_name?: string
  company_name?: string
  job_title?: string
  dba?: string

  // Census Page
  census_format?: 'manual' | 'payroll' | 'csv' | null
  census_employees?: EmployeeInfo[]

  // Additional Info Page
  industry?: string
  employee_count?: number
  estimated_employee_count_in_one_year?: number

  // Benefits
  benefits?: {
    target_individual_deductible?: number
    target_individual_oop_max?: number
    target_network_type?: string
    target_metal_tier?: string
    coverage_start_date?: string
  }

  // Pay Schedule
  payFrequency?: string
  payScheduleName?: string
  firstPayDay?: number
  secondPayDay?: number
  referencePayDate?: string
  bankClosureStrategy?: BankClosureStrategy,

  // Remaining Info Page
  address_line1?: string
  business_type?: string
  city?: string
  ein?: string
  name?: string
  number_of_employees?: number
  phone_number?: string
  state?: string
  zip?: string
}

interface PrefillContextType {
  prefillData: PrefillData | null
  isPrefillEnabled: boolean
  getPrefillValue: <K extends keyof PrefillData>(key: K) => PrefillData[K] | undefined
}

const PrefillContext = createContext<PrefillContextType>({
  prefillData: null,
  isPrefillEnabled: false,
  getPrefillValue: () => undefined,
})

const demoPrefillData: PrefillData = {
  email: `demo-${Date.now()}@thatch.com`,
  password: 'demo-2025-password',
  password_confirmation: 'demo-2025-password',
  agree_to_e_sign_agreement: true,
  agree_to_terms: true,

  first_name: 'Jane',
  last_name: 'Doe',
  job_title: 'CEO',
  company_name: 'Acme Co LLC',
  dba: 'Acme',

  census_format: 'manual',
  census_employees: [
    {
      first_name: 'Alice',
      last_name: 'Johnson',
      email: `alice.johnson-${Date.now()}@acme.com`,
      name: 'Alice Johnson',
      date_of_birth: '1985-03-15',
      zip: '94105',
      dependents: [
        { relationship: 'spouse', date_of_birth: '1983-07-22' },
        { relationship: 'child', date_of_birth: '2010-11-08' }
      ]
    },
    {
      first_name: 'Bob',
      last_name: 'Smith',
      email: `bob.smith-${Date.now()}@acme.com`,
      name: 'Bob Smith',
      date_of_birth: '1990-12-03',
      zip: '94105',
      dependents: []
    },
    {
      first_name: 'Carol',
      last_name: 'Davis',
      email: `carol.davis-${Date.now()}@acme.com`,
      name: 'Carol Davis',
      date_of_birth: '1988-05-20',
      zip: '94105',
      dependents: [
        { relationship: 'child', date_of_birth: '2015-09-14' }
      ]
    },
    {
      first_name: 'Diane',
      last_name: 'Jones',
      email: `diane.jones-${Date.now()}@acme.com`,
      name: 'Diane Jones',
      date_of_birth: '2000-01-01',
      zip: '94105',
      dependents: [
        { relationship: 'child', date_of_birth: '2025-01-01' }
      ]
    },
    {
      first_name: 'Ethan',
      last_name: 'Brown',
      email: `ethan.brown-${Date.now()}@acme.com`,
      name: 'Ethan Brown',
      date_of_birth: '2005-07-10',
      zip: '94105',
      dependents: []
    }
  ],

  industry: 'educational_services',
  employee_count: 3,
  estimated_employee_count_in_one_year: 5,

  benefits: {
    target_individual_deductible: 1000,
    target_individual_oop_max: 5000,
    target_network_type: 'ppo',
    target_metal_tier: 'silver',
  },

  payFrequency: 'semi_monthly',
  payScheduleName: 'Semi-Monthly (2025)',
  firstPayDay: 15,
  secondPayDay: 30,
  bankClosureStrategy: BankClosureStrategy.closest,

  address_line1: '123 Main St',
  business_type: 'llc',
  city: 'Anytown',
  ein: '12-3456789',
  name: 'Acme Co LLC',
  number_of_employees: 3,
  phone_number: '123-456-7890',
  state: 'CA',
  zip: '12345',
}

export function usePrefill () {
  return useContext(PrefillContext)
}

interface PrefillProviderProps {
  children: React.ReactNode | ((props: PrefillContextType) => React.ReactNode)
}

export function PrefillProvider ({ children }: PrefillProviderProps) {
  const { flags } = useFlags()

  const isPrefillEnabled = flags.enablePrefillData || false

  const prefillData: PrefillData | null = isPrefillEnabled ? demoPrefillData : null

  const getPrefillValue = <K extends keyof PrefillData>(key: K): PrefillData[K] | undefined => {
    return prefillData?.[key]
  }

  const value = {
    prefillData,
    isPrefillEnabled,
    getPrefillValue,
  }

  return (
    <PrefillContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </PrefillContext.Provider>
  )
}
